package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.put
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.AddFavoriteRequest
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.CropData
import or.garden.manager.domain.model.CropRequest
import or.garden.manager.domain.model.CropWithTranslations

internal class CropApi {
    suspend fun getCrops(searchQuery: String? = null): Result<HttpResponse> = runCatching {
        val url = if (searchQuery != null) {
            "${GardenHttpClient.baseUrl}/crops?search=$searchQuery"
        } else {
            "${GardenHttpClient.baseUrl}/crops"
        }
        return@runCatching GARDEN_CLIENT.get(url)
    }

    suspend fun getFavourites(): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get(
            "${GardenHttpClient.baseUrl}/crops/favourite"
        )
    }

    // Add a favourite for a crop (POST /crops/{id}/favourite)
    suspend fun addFavourite(cropId: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.put(
            "${GardenHttpClient.baseUrl}/crops/favourite"
        ) {
            setBody(
                AddFavoriteRequest(cropId)
            )
        }
    }

    // Remove a favourite for a crop (DELETE /crops/{id}/favourite)
    suspend fun removeFavourite(cropId: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.delete(
            "${GardenHttpClient.baseUrl}/crops/favourite/$cropId"
        )
    }

    suspend fun createCrop(request: CropRequest): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/crops") {
            setBody(request)
        }
    }

    suspend fun updateCrop(id: Int, request: CropRequest): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.put("${GardenHttpClient.baseUrl}/crops/$id") {
            setBody(request)
        }
    }

    suspend fun getCropFull(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get("${GardenHttpClient.baseUrl}/crops/$id/full")
    }

    suspend fun fillWithAI(cropName: String): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post("${GardenHttpClient.baseUrl}/crops/fill-ai") {
            setBody(mapOf("cropName" to cropName))
        }
    }
}

private data object CropRepository {
    fun getCrops(searchQuery: String? = null): Flow<ResultState<ApiResponse<List<CropData>>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi()
                .getCrops(searchQuery)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to load crops. Please try again!"))
        }
    }

    fun getFavourites(): Flow<ResultState<ApiResponse<List<CropData>>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi()
                .getFavourites()
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (_: Exception) {
            emit(ResultState.Error("Failed to load favourites. Please try again!"))
        }
    }

    // New: add favourite
    fun addFavourite(cropId: Int): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi()
                .addFavourite(cropId)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to add to favourites. Please try again!"))
        }
    }

    // New: remove favourite
    fun removeFavourite(cropId: Int): Flow<ResultState<Unit>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi()
                .removeFavourite(cropId)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(Unit))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("Failed to remove from favourites. Please try again!"))
        }
    }

    fun createCrop(request: CropRequest): Flow<ResultState<ApiResponse<CropData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi().createCrop(request).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to create crop"))
        }
    }

    fun updateCrop(id: Int, request: CropRequest): Flow<ResultState<ApiResponse<Unit>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi().updateCrop(id, request).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to update crop"))
        }
    }

    fun getCropFull(id: Int): Flow<ResultState<ApiResponse<CropWithTranslations>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi().getCropFull(id).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to load full crop data"))
        }
    }

    fun fillWithAI(cropName: String): Flow<ResultState<ApiResponse<CropWithTranslations>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = CropApi().fillWithAI(cropName).getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to fill with AI"))
        }
    }
}

data object CropService {
    fun getCrops(
        searchQuery: String? = null,
        result: (ResultState<ApiResponse<List<CropData>>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .getCrops(searchQuery)
                .collect {
                    result(it)
                }
        }
    }

    fun getFavourites(result: (ResultState<ApiResponse<List<CropData>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .getFavourites()
                .collect {
                    result(it)
                }
        }
    }

    // New service wrapper for add favourite
    fun addFavourite(cropId: Int, result: (ResultState<Unit>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .addFavourite(cropId)
                .collect {
                    result(it)
                }
        }
    }

    // New service wrapper for remove favourite
    fun removeFavourite(cropId: Int, result: (ResultState<Unit>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .removeFavourite(cropId)
                .collect {
                    result(it)
                }
        }
    }

    fun createCrop(request: CropRequest, result: (ResultState<ApiResponse<CropData>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .createCrop(request)
                .collect {
                    result(it)
                }
        }
    }

    fun updateCrop(id: Int, request: CropRequest, result: (ResultState<ApiResponse<Unit>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .updateCrop(id, request)
                .collect {
                    result(it)
                }
        }
    }

    fun getCropFull(id: Int, result: (ResultState<ApiResponse<CropWithTranslations>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .getCropFull(id)
                .collect {
                    result(it)
                }
        }
    }

    fun fillWithAI(cropName: String, result: (ResultState<ApiResponse<CropWithTranslations>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            CropRepository
                .fillWithAI(cropName)
                .collect {
                    result(it)
                }
        }
    }
}
